/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import FacebookLeadAdsForm, { FacebookLeadAdsFormStatus } from 'cio/models/facebook-lead-ads-form';
import AuthService from 'cio/services/auth';
import { task } from 'ember-concurrency';
import AnalyticsService from 'cio/services/analytics';
import FacebookLeadAdsService from 'cio/services/facebook-lead-ads';
import AccountService from 'cio/services/account';

export enum Tabs {
  ChooseForm = 'choose-form',
  MapFormFields = 'map-form-fields',
  FormSettings = 'form-settings',
  Success = 'success',
}

interface FormsFacebookLeadAdsFormModalArgs {
  onClose: () => unknown;
  onConnectForm?: (form: FacebookLeadAdsForm) => unknown;
  onSelectForm?: (form: FacebookLeadAdsForm) => unknown;
  form?: FacebookLeadAdsForm;
  isDetailsPage?: boolean;
  source: string;
}

export default class FormsFacebookLeadAdsFormModal extends Component<FormsFacebookLeadAdsFormModalArgs> {
  @service auth!: AuthService;
  @service analytics!: AnalyticsService;
  @service facebookLeadAds!: FacebookLeadAdsService;
  @service account!: AccountService;

  tabs = Tabs;
  cheerImage = new URL('cio/assets/images/cheer.png', import.meta.url).toString();

  @tracked currentTab!: Tabs;
  @tracked conversionTracking = false;

  constructor(owner: unknown, args: FormsFacebookLeadAdsFormModalArgs) {
    super(owner, args);

    this.goToTab(!this.args.form ? Tabs.ChooseForm : Tabs.MapFormFields);
  }

  get navTabs() {
    let navTabs: { name: string; valid: boolean; label: string; disabled: boolean }[] = [];

    if (!this.args.isDetailsPage) {
      navTabs = [...navTabs, { name: Tabs.ChooseForm, valid: !!this.args.form, label: 'Choose Form', disabled: false }];
    }

    navTabs = [
      ...navTabs,
      {
        name: Tabs.MapFormFields,
        valid:
          this.currentTab !== Tabs.ChooseForm && (this.args.form ? !this.args.form.hasDuplicateFieldMapping : false),
        label: 'Map Form Fields',
        disabled: !this.args.form,
      },
      {
        name: Tabs.FormSettings,
        valid: this.currentTab !== Tabs.ChooseForm && this.currentTab !== Tabs.MapFormFields,
        label: 'Form Settings',
        disabled: !this.args.form,
      },
    ];

    return navTabs;
  }

  get submitError() {
    if (!this.args.form) {
      return 'Must choose a form.';
    }

    if (this.args.form?.hasDuplicateFieldMapping) {
      return "Can't map more than one field to the same profile attribute.";
    }

    return null;
  }

  @computed('facebookLeadAds.connectableDisconnectedForms.[]')
  get formsToChoose() {
    return [
      { value: undefined, label: 'Choose a form', disabled: true },
      ...this.facebookLeadAds.connectableDisconnectedForms.map((f) => ({
        value: f.id,
        label: f.formNameForDisplay,
        disabled: false,
      })),
    ];
  }

  get canSubmit() {
    if (!this.args.form?.get('isNew')) {
      return this.auth.hasPermission('journeys/forms/edit');
    }

    return this.auth.hasPermission('journeys/forms/create');
  }

  submitTask = task(async (event: Event) => {
    event.preventDefault();
    // this.args.form is always present when submit button gets pressed: assert it for TypeScript
    if (!this.args.form) {
      return;
    }

    // connecting a form: navigate between tabs, save, or close the modal - as needed
    if (this.currentTab === Tabs.ChooseForm) {
      this.goToTab(Tabs.MapFormFields);
    } else if (this.currentTab === Tabs.MapFormFields) {
      this.goToTab(Tabs.FormSettings);
    } else if (this.currentTab === Tabs.FormSettings) {
      try {
        this.args.form.set('status', FacebookLeadAdsFormStatus.Active);

        await this.facebookLeadAds.connectForm(this.args.form);
        if (this.args.onConnectForm) {
          this.args.onConnectForm(this.args.form);
        }

        this.goToTab(Tabs.Success);
      } catch (e) {
        this.args.form.set('status', FacebookLeadAdsFormStatus.Inactive);
        throw e;
      }
    } else if (this.currentTab === Tabs.Success) {
      return this.args.onClose();
    }

    return;
  });

  @action
  goToTab(tab: Tabs) {
    if (tab === Tabs.MapFormFields) {
      this.analytics.track('forms', 'setup_map_form_fields_tab_viewed', {
        from: this.args.source,
      });
    }

    this.currentTab = tab;
  }

  @action handleFormSelect(id: string) {
    return this.args.onSelectForm?.(this.facebookLeadAds.forms.findBy('id', id) as FacebookLeadAdsForm);
  }

  @action
  toggleConversionTracking() {
    this.conversionTracking = !this.conversionTracking;
  }
}
